/**
 * Interwork Marketing — Русский
 */
import type { Dictionary } from "../types";

export const ru: Dictionary = {
  common: {
    loading: "Загрузка...",
    error: "Произошла ошибка",
    retry: "Повторить",
    save: "Сохранить",
    cancel: "Отмена",
    delete: "Удалить",
    edit: "Редактировать",
    close: "Закрыть",
    confirm: "Подтвердить",
    search: "Поиск",
    filter: "Фильтр",
    filters: "Фильтры",
    export: "Экспорт CSV",
    refresh: "Обновить",
    back: "Назад",
    next: "Далее",
    previous: "Назад",
    submit: "Отправить",
    actions: "Действия",
    yes: "Да",
    no: "Нет",
    empty: "Нет данных",
    all: "Все",
    active: "Активные",
    paused: "На паузе",
    archived: "Архив",
    total: "Итого",
    today: "Сегодня",
    yesterday: "Вчера",
    week: "Неделя",
    month: "Месяц",
    last7: "Последние 7 дней",
    last30: "Последние 30 дней",
    last90: "Последние 90 дней",
    overall: "Всё время",
    settings: "Настройки",
    logout: "Выйти",
    language: "Язык",
    languageUz: "Узбекский",
    languageRu: "Русский",
    languageEn: "English",
    openMenu: "Открыть меню",
    closeMenu: "Закрыть меню",
    popular: "Избранное",
    more: "Ещё",
    noData: "Нет данных",
    sync: "Синхронизация",
    syncStarted: "Синхронизация запущена",
    details: "Подробнее",
    status: "Статус",
    name: "Название",
    date: "Дата",
    period: "Период",
  },
  nav: {
    home: "Главная",
    sales: "Продажи",
    marketing: "Маркетинг",
    chatbot: "Чат-бот",
    coach: "Коуч",
    settings: "Настройки",
    navigation: "Навигация",
    salesData: "Данные продаж",
    company: "Компания",
    integration: "Интеграции",
    analysis: "Аналитика",
    knowledge: "База знаний",
  },
  auth: {
    login: "Войти",
    loggingIn: "Вход...",
    username: "Пользователь",
    password: "Пароль",
    usernameRequired: "Введите имя пользователя",
    passwordMin: "Пароль не менее 8 символов",
    loginError: "Ошибка входа. Попробуйте снова.",
    showPassword: "Показать пароль",
    hidePassword: "Скрыть пароль",
    welcome: "Добро пожаловать",
    subtitle: "Вход в Interwork Marketing",
  },
  marketing: {
    module: "Маркетинг",
    nav: {
      dashboard: "Дашборд",
      campaigns: "Кампании",
      creatives: "Креативы",
      scenarios: "Сценарии",
      competitors: "Конкуренты",
      stories: "Stories",
      clients: "Клиенты",
      summaries: "Сводки",
      adAccounts: "Рекламные аккаунты",
    },
    dashboard: {
      title: "Дашборд",
      breadcrumb: "Маркетинг",
      emptyTitle: "Нет данных",
      emptyDesc: "За выбранный период нет показателей. Синхронизируйте аккаунт.",
      spend: "Расход",
      leads: "Лиды",
      cpl: "CPL",
      roas: "ROAS",
      impressions: "Показы",
      clicks: "Клики",
      ctr: "CTR",
      sync: "Синхронизация",
    },
    campaigns: {
      title: "Кампании",
      description: "Meta spend + CRM (SalesLead · fb ad tags)",
      searchPlaceholder: "Название кампании...",
      notFound: "Кампании не найдены",
      notFoundDesc: "Измените фильтры или дождитесь синхронизации",
      noActive: "Нет активных кампаний",
      noActiveDesc: "Пока нет активных кампаний",
      activeSection: "Активные кампании",
      inactiveSection: "На паузе / архив",
      wonDynamics: "Won dynamics — по дням",
      campaignsCount: "{{count}} кампаний · Meta spend + CRM",
      col: {
        campaign: "Кампания",
        spend: "Spend",
        leads: "Leads",
        cpl: "CPL",
        act: "Act",
        won: "Won",
        lost: "Lost",
        qual: "Qual",
        costWon: "Cost/Won",
        convRate: "Conv.rate",
        qualRate: "Qual.rate",
        dealTime: "Deal time",
      },
      totalRow: "Итого ({{count}} кампаний)",
      back: "Кампании",
      overview: "Обзор",
      creativesTab: "Креативы",
      summaryTab: "Сводка",
    },
    creatives: {
      title: "Креативы",
      description: "Библиотека объявлений — активные рекламы, креативы и статусы",
      lastSync: "последняя синхронизация",
      filters: "Фильтры",
      results: "~{{count}} результатов",
      empty: "Креативы не найдены",
      emptyDesc: "Измените фильтр или период",
      allCampaigns: "Все кампании",
    },
    competitors: {
      title: "Конкуренты",
      description: "Instagram-конкуренты: бенчмарк, engagement и AI-анализ",
      add: "Добавить конкурента",
      addFirst: "Добавьте первого конкурента",
      addFirstDesc:
        "Укажите Instagram-профиль — мы подготовим бенчмарк, топ reels и AI-анализ.",
      emptyCompare: "Нет конкурентов для сравнения",
      emptyCompareDesc: "Сначала добавьте конкурента для анализа «мы vs они».",
      notConfigured: "Анализ конкурентов не настроен",
      notConfiguredDesc:
        "Apify не подключён для Instagram. Обратитесь к администратору.",
      own: "Наш аккаунт",
      scrape: "Обновить",
      deepScrape: "Deep scrape",
      followers: "Подписчики",
      posts: "Посты",
      engagement: "Engagement",
      avgViews: "Ср. просмотры",
      videoAnalysis: "AI-анализ видео",
      compare: "Сравнить",
      username: "Instagram username",
      displayName: "Отображаемое имя",
      isOwn: "Это наш аккаунт",
    },
    stories: {
      title: "Архив Stories",
      description: "Сохранение и просмотр Instagram Stories",
    },
    scenarios: {
      title: "Сценарии",
      description: "Рекламные сценарии на базе AI",
    },
    clients: {
      title: "Клиенты",
      description: "Клиенты Sales CRM и сегменты",
      search: "Поиск клиентов...",
      empty: "Клиенты не найдены",
    },
    summaries: {
      title: "Сводки",
      description: "AI-сводки по продажам",
    },
    status: {
      ACTIVE: "Активна",
      PAUSED: "Пауза",
      ARCHIVED: "Архив",
      DELETED: "Удалена",
    },
  },
  period: {
    today: "Сегодня",
    week: "Неделя",
    month: "Месяц",
    overall: "Всё время",
    custom: "Свой период",
  },
  legal: {
    privacy: {
      title: "Политика конфиденциальности",
      lastUpdated: "Обновлено: 2026-05-07",
    },
    terms: {
      title: "Условия использования",
      lastUpdated: "Обновлено: 2026-05-07",
    },
    deletion: {
      title: "Запрос на удаление данных",
      description:
        "Здесь вы можете отправить запрос на удаление данных, собранных через Meta.",
      submit: "Отправить запрос",
      success: "Запрос принят",
    },
  },
  chatbot: {
    nav: {
      dashboard: "Дашборд",
      inbox: "Входящие",
      analytics: "Аналитика",
      leads: "Лиды",
      knowledge: "База знаний",
      channels: "Каналы",
      demo: "Демо",
    },
    dashboard: {
      title: "Дашборд чат-бота",
      subtitle: "Метрики чат-бота и воронка лидов в реальном времени",
      kpi: {
        activeConversations: "Активные диалоги",
        avgResponseTime: "Среднее время ответа",
        conversionRate: "Конверсия",
        totalLeads: "Всего лидов",
      },
      sections: {
        funnel: "Реклама → Чат → Лид → Сделка",
        recentConversations: "Недавние диалоги",
        aiAcceptRate: "Принятие AI-подсказок",
        channelBreakdown: "По каналам",
      },
      empty: "Пока нет диалогов",
    },
    analytics: {
      title: "Аналитика чат-бота",
      subtitle: "Глубокий анализ и экспорт",
      filters: { period: "Период", channel: "Канал", agent: "Оператор" },
      charts: {
        dailyVolume: "Дневной объём диалогов",
        responseTime: "Распределение времени ответа",
        intentBreakdown: "Распределение intent",
        topFaq: "Топ FAQ",
        aiHumanRatio: "AI vs оператор",
        leadTimeline: "Динамика конверсии лидов",
      },
    },
    leads: {
      title: "Лиды чат-бота",
      subtitle: "Все лиды, созданные через чат-бот",
      columns: {
        contact: "Клиент",
        intent: "Intent",
        channel: "Канал",
        amoStatus: "AmoCRM",
        createdAt: "Создан",
        source: "Источник",
      },
      amoStatus: {
        synced: "Синхронизирован",
        pending: "Ожидание",
        failed: "Ошибка",
        notSynced: "Не синхронизирован",
      },
    },
    funnel: { ad: "Реклама", chat: "Чат", lead: "Лид", deal: "Сделка" },
    intent: {
      FAQ: "FAQ",
      BUYER_INTENT: "Намерение купить",
      OBJECTION: "Возражение",
      COMPLAINT: "Жалоба",
      SMALL_TALK: "Small talk",
      OTHER: "Другое",
    },
    demo: {
      title: "Meta App Review demo",
      banner: "Meta Reviewer Demo Mode — English UI",
      steps: {
        connect: "Connect Instagram",
        selectAccount: "Select Business account",
        inbox: "View Inbox (live messages)",
        reply: "Accept AI suggestion + Reply",
        verify: "Verify on Instagram app",
        comments: "Comment management",
      },
      next: "Следующий шаг",
      done: "Готово",
    },
  },
  errors: {
    generic: "Неожиданная ошибка",
    network: "Ошибка сети. Попробуйте снова",
    forbidden: "Нет доступа",
    notFound: "Не найдено",
    server: "Ошибка сервера",
  },
  ai: {
    assistant: "AI-помощник",
    ask: "Задайте вопрос...",
    thinking: "Думаю...",
  },
  sales: {
    contacts: "Контакты",
    pipelines: "Воронки",
    deals: "Сделки",
    calls: "Звонки",
    custdev: "CustDev",
    managers: "Менеджеры",
    leaderboard: "Рейтинг",
    plans: "Планы",
    voronka: "Аудит воронки",
    playbook: "Playbook",
    todayCalls: "Звонки сегодня",
    activeDeals: "Активные сделки",
  },
  coach: {
    hub: "Центр",
    lessons: "Уроки",
    chat: "Чат",
    attendance: "Активность",
    certificates: "Сертификаты",
    exam: "Voice Exam",
    examStart: "Начать экзамен",
    history: "История",
    scenarios: "Сценарии",
    stats: "Статистика",
  },
  settings: {
    general: "Общие",
    branding: "Брендинг",
    integrations: "Интеграции",
    telegram: "Telegram",
    salesCriteria: "Критерии продаж",
    aiCosts: "Расходы AI",
  },
};
